import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Plus, Trash2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import ItemPicker from '../components/ItemPicker'
import MoneyInput from '../components/MoneyInput'
import {
  draftRequested, newManualRow, percentDone, requestedFromPercent, reviewOf, rowFigures, rowSections,
  type ApplicationTotals, type InvestorWarning, type DraftAmounts, type DraftReview, type DraftReviews, type ManualRow, type RowsMode,
} from '../lib/applications'
import { compareMoneyStrings } from '../lib/contracts'
import { formatMoney, moneyLocale, parseMoneyInput, toEditText } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostApplicationRow, CostApplicationStatus } from '../types'

export interface RowsTableProps {
  rows: CostApplicationRow[]
  /** The application has rows, so an empty `rows` means the filter hides them all. */
  hasRows: boolean
  mode: RowsMode
  status: CostApplicationStatus
  /** Show the certified amount and the reason (also on a returned draft, read-only). */
  showReview: boolean
  draft: DraftAmounts
  review: DraftReviews
  totals: ApplicationTotals
  retentionPercent: string | null
  disabled: boolean
  currency: string
  locale: string
  onDraft: (key: string, value: string | null, valid: boolean) => void
  onReview: (row: CostApplicationRow, patch: Partial<DraftReview>) => void
  /** The editable manual rows (draft and review); `rows` then holds the regular ones only. */
  manual: ManualRow[]
  /** The budget items the contract allocates: the only ones a manual row can pair with. */
  allowedNodeIds: string[]
  tree: CostTree
  onManual: (rows: ManualRow[]) => void
  /** SUB only: items billed to subcontractors above the investor billing, by node. */
  investorWarnings?: Map<string, InvestorWarning>
}

let manualCounter = 0

/** A cumulative % typed as text and committed on blur, so rounding never rewrites what is being typed. */
export const PercentCell: React.FC<{ value: string; disabled: boolean; label: string; className?: string; onCommit: (pct: string) => void }> = ({ value, disabled, label, className, onCommit }) => {
  const { i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const [text, setText] = useState(() => toEditText(value, locale))
  const [invalid, setInvalid] = useState(false)
  useEffect(() => { setText(toEditText(value, locale)); setInvalid(false) }, [value, locale])
  return (
    <Input
      value={text}
      inputMode="decimal"
      disabled={disabled}
      aria-label={label}
      aria-invalid={invalid || undefined}
      className={cn('h-8 text-right tabular-nums', invalid && 'border-destructive focus-visible:ring-destructive', className)}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        // An unchanged text must not commit: the 2-dp % round trip would rewrite the period amount.
        if (text === toEditText(value, locale)) return
        const parsed = parseMoneyInput(text)
        if (!parsed.ok) { setInvalid(true); return }
        onCommit(parsed.value ?? '0.00')
      }}
    />
  )
}

export const Total: React.FC<{ label: string; value: string; columns: number; money: (v: string) => string; strong?: boolean }> = ({ label, value, columns, money, strong }) => (
  <TableRow>
    <TableCell colSpan={columns - 1} className="text-right">{label}</TableCell>
    <TableCell className={cn('text-right tabular-nums', strong && 'font-semibold')}>{money(value)}</TableCell>
  </TableRow>
)

/** The "Další položky" rows of a draft or a review: penalties and deductions with an optional budget item. */
export const ManualRows: React.FC<{
  mode: 'draft' | 'review'; manual: ManualRow[]; showReview: boolean; disabled: boolean; columns: number; tree: CostTree; allowedNodeIds: string[]
  money: (v: string) => string; onManual: (rows: ManualRow[]) => void
}> = ({ mode, manual, showReview, disabled, columns, tree, allowedNodeIds, money, onManual }) => {
  const { t } = useTranslation()
  const focusId = useRef<string | null>(null)
  const patch = (id: string, change: Partial<ManualRow>) => onManual(manual.map((m) => (m.id === id ? { ...m, ...change } : m)))
  const nodeLabel = (id: string | null, fallback: string | null) => {
    const n = id ? tree.byId.get(id)?.activity : undefined
    if (n) return n.code ? `${n.code} · ${n.name}` : n.name
    return id ? fallback ?? '—' : undefined
  }
  return (
    <>
      <TableRow className="bg-muted/40 hover:bg-muted/40">
        <TableCell colSpan={columns} className="py-1.5 text-xs font-semibold text-muted-foreground">
          <div className="flex items-center justify-between">
            {t('CostControl_application_manual_section')}
            <Button variant="outline" size="sm" className="h-7" disabled={disabled} onClick={() => { const row = newManualRow(`new:${manualCounter++}`); focusId.current = row.id; onManual([...manual, row]) }}>
              <Plus className="h-4 w-4" />
              {t('CostControl_application_manual_add')}
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {manual.map((m) => {
        const locked = mode === 'review' && m.contractor
        return (
          <TableRow key={m.id}>
            <TableCell>
              <Input
                ref={m.id === focusId.current ? (el) => { if (el) { focusId.current = null; el.focus(); el.scrollIntoView({ block: 'nearest' }) } } : undefined}
                value={m.description}
                onChange={(e) => patch(m.id, { description: e.target.value })}
                disabled={disabled || locked}
                maxLength={200}
                placeholder={t('CostControl_application_manual_description')}
                aria-label={t('CostControl_application_manual_description')}
                className="h-8"
              />
              <div className="mt-1 flex items-center gap-1">
                {tree.order.length ? (
                  <ItemPicker variant="select" tree={tree} excludeIds={[]} onlyIds={allowedNodeIds} disabled={disabled} label={nodeLabel(m.nodeId, m.label)} onPick={(nodeId) => patch(m.id, { nodeId })} />
                ) : (
                  <span className="text-sm text-muted-foreground">{m.label ?? '—'}</span>
                )}
                {m.nodeId && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={disabled} aria-label={t('CostControl_application_manual_item')} onClick={() => patch(m.id, { nodeId: null })}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right text-muted-foreground">—</TableCell>
            <TableCell className="text-right text-muted-foreground">—</TableCell>
            <TableCell className="text-right">
              {mode === 'draft'
                ? <MoneyInput allowNegative value={m.amount} onChange={(amount, valid) => patch(m.id, { amount, valid })} disabled={disabled} className="h-8" aria-label={t('CostControl_application_col_requested')} />
                : <span className="tabular-nums">{m.contractor ? money(m.requested) : '—'}</span>}
            </TableCell>
            <TableCell className="text-right text-muted-foreground">—</TableCell>
            {showReview && (
              <TableCell className="text-right">
                {mode === 'review'
                  ? <MoneyInput allowNegative value={m.amount} onChange={(amount, valid) => patch(m.id, { amount, valid })} disabled={disabled} className="h-8" aria-label={t('CostControl_application_col_certified')} />
                  : <span className="text-muted-foreground">—</span>}
              </TableCell>
            )}
            {showReview && (
              <TableCell>
                {mode === 'review' && <Input value={m.note} onChange={(e) => patch(m.id, { note: e.target.value })} disabled={disabled} className="h-8" aria-label={t('CostControl_application_col_note')} />}
              </TableCell>
            )}
            <TableCell className="text-right">
              {!locked && (
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={disabled} aria-label={t('CostControl_application_manual_remove')} onClick={() => onManual(manual.filter((x) => x.id !== m.id))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

/**
 * The rows of an application by section (1b-A spec §12): value, certified before, this period in Kč or as a cumulative
 * %, the reviewer's amount and reason, and what remains. ponytail: a plain table, as the price editor (~200 rows).
 */
const ApplicationRowsTable: React.FC<RowsTableProps> = ({ rows, hasRows, mode, status, showReview, draft, review, totals, retentionPercent, disabled, currency, locale, onDraft, onReview, manual, allowedNodeIds, tree, onManual, investorWarnings }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  const editing = mode !== 'view'
  const warningText = (w: InvestorWarning) => t('CostControl_billing_review_row', { subcontractor: money(w.subcontractor), investor: money(w.investorBilled) })
  if (!rows.length && !editing) {
    return <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t(hasRows ? 'CostControl_application_rows_no_match' : 'CostControl_application_rows_empty')}</p>
  }
  const columns = showReview ? 8 : 6
  return (
    <div className="rounded-md border">
      <Table containerClassName="max-h-[70vh]">
        <TableHeader className="sticky top-0 z-10 bg-background">
          <TableRow>
            <TableHead className="min-w-64">{t('CostControl_application_col_item')}</TableHead>
            <TableHead className="w-32 text-right">{t('CostControl_application_col_value')}</TableHead>
            <TableHead className="w-32 text-right">{t('CostControl_application_col_previous')}</TableHead>
            <TableHead className="w-36 text-right">{t('CostControl_application_col_requested')}</TableHead>
            <TableHead className="w-28 text-right">{t('CostControl_application_col_percent')}</TableHead>
            {showReview && <TableHead className="w-44 text-right">{t('CostControl_application_col_certified')}</TableHead>}
            {showReview && <TableHead className="min-w-48">{t('CostControl_application_col_note')}</TableHead>}
            <TableHead className="w-32 text-right">{t('CostControl_application_col_remaining')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rowSections(rows).map((s) => (
            <React.Fragment key={s.key}>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell colSpan={columns} className="py-1.5 text-xs font-semibold text-muted-foreground">
                  {s.manual ? t('CostControl_application_manual_section') : s.changes ? t('CostControl_application_section_changes') : s.label || t('CostControl_application_section_none')}
                </TableCell>
              </TableRow>
              {s.rows.map((r) => {
                const requested = draftRequested(r, draft) ?? '0.00'
                const rv = reviewOf(r, review)
                const { certified, counted, lowered, remaining, reviewable } = rowFigures(r, requested, rv, mode, status)
                if (r.kind === 'MANUAL') {
                  return (
                    <TableRow key={r.key}>
                      <TableCell>
                        <div>{r.description}</div>
                        <div className="text-xs text-muted-foreground">{r.nodeId ? (r.code ? `${r.code} · ${r.name}` : r.name) : '—'}</div>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                      <TableCell className="text-right tabular-nums">{money(requested)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                      {showReview && <TableCell className="text-right tabular-nums">{certified === null ? '—' : money(certified)}</TableCell>}
                      {showReview && <TableCell><span className="text-sm">{r.note ?? ''}</span></TableCell>}
                      <TableCell className="text-right text-muted-foreground">—</TableCell>
                    </TableRow>
                  )
                }
                const warning = r.nodeId ? investorWarnings?.get(r.nodeId) : undefined
                return (
                  <TableRow key={r.key}>
                    <TableCell>
                      {r.kind === 'CHANGE_LINE' && <div className="text-xs text-muted-foreground">{t('CostControl_application_change_row', { number: r.changeNumber, title: r.changeTitle })}</div>}
                      <div className="flex items-center gap-1.5">
                        {r.code ? `${r.code} · ${r.name}` : r.name}
                        {warning && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={0} role="img" aria-label={warningText(warning)} className="inline-flex shrink-0"><AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" /></span>
                              </TooltipTrigger>
                              <TooltipContent>{warningText(warning)}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      {r.quantity !== null && <div className="text-xs text-muted-foreground">{`${r.quantity} ${r.unit ?? ''}`.trim()}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.value)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground"><div>{money(r.previous)}</div>
                      <div className="text-xs">{`${toEditText(percentDone(r.value, '0', r.previous), locale)} %`}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {mode === 'draft'
                        ? <MoneyInput value={draftRequested(r, draft)} onChange={(value, valid) => onDraft(r.key, value, valid)} disabled={disabled} className="h-8" aria-label={t('CostControl_application_col_requested')} />
                        : <span className="tabular-nums">{money(requested)}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {mode === 'draft'
                        ? <PercentCell value={percentDone(r.value, r.previous, requested)} disabled={disabled} label={t('CostControl_application_col_percent')} onCommit={(pct) => onDraft(r.key, requestedFromPercent(r.value, r.previous, pct), true)} />
                        : <span className="tabular-nums">{`${toEditText(percentDone(r.value, r.previous, counted), locale)} %`}</span>}
                    </TableCell>
                    {showReview && (
                      <TableCell className="text-right">
                        {reviewable
                          ? <MoneyInput value={rv.certified} onChange={(value, valid) => onReview(r, { certified: value, valid })} disabled={disabled} className="h-8" aria-label={t('CostControl_application_col_certified')} />
                          : <span className="tabular-nums">{certified === null ? '—' : money(certified)}</span>}
                        {lowered && <Badge variant="warning" className="mt-1">{t('CostControl_application_lowered')}</Badge>}
                      </TableCell>
                    )}
                    {showReview && (
                      <TableCell>
                        {reviewable
                          ? <Input value={rv.note} onChange={(e) => onReview(r, { note: e.target.value })} disabled={disabled} className="h-8" aria-label={t('CostControl_application_col_note')} />
                          : <span className="text-sm">{r.note ?? ''}</span>}
                      </TableCell>
                    )}
                    <TableCell className={cn('text-right tabular-nums', compareMoneyStrings(remaining, '0') < 0 && 'text-destructive')}>{money(remaining)}</TableCell>
                  </TableRow>
                )
              })}
            </React.Fragment>
          ))}
          {mode !== 'view' && (
            <ManualRows mode={mode} manual={manual} showReview={showReview} disabled={disabled} columns={columns} tree={tree} allowedNodeIds={allowedNodeIds} money={money} onManual={onManual} />
          )}
        </TableBody>
        <TableFooter className="sticky bottom-0 z-10 bg-muted">
          <Total columns={columns} money={money} label={t('CostControl_application_total_requested')} value={totals.requested} />
          {status !== 'DRAFT' && <Total columns={columns} money={money} label={t('CostControl_application_total_certified')} value={totals.certified} strong />}
          {retentionPercent !== null && <Total columns={columns} money={money} label={t('CostControl_invoice_field_retention', { percent: toEditText(retentionPercent, locale) })} value={totals.retention} />}
          <Total columns={columns} money={money} label={t('CostControl_invoice_field_payable')} value={totals.payable} strong />
        </TableFooter>
      </Table>
    </div>
  )
}
export default ApplicationRowsTable
